import { Link, NavLink, Outlet } from "react-router-dom";
import {
  LayoutGrid,
  Users,
  Images,
  Settings,
  LogOut,
  Plus,
} from "lucide-react";
import { signOut, useSession } from "@/features/auth/use-session";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

const navItems = [
  { to: "/collections", label: "Collections", icon: LayoutGrid },
  { to: "/timeline", label: "Timeline", icon: Images, disabled: true },
  { to: "/people", label: "People", icon: Users, disabled: true },
];

export function AppLayout() {
  const { data: session } = useSession();
  const initial = (session?.user?.name ?? session?.user?.email ?? "?")
    .slice(0, 1)
    .toUpperCase();

  return (
    <div className="grid min-h-svh grid-cols-[240px_1fr] bg-background">
      <aside className="flex flex-col border-r border-border bg-sidebar text-sidebar-foreground">
        <Link to="/collections" className="flex h-14 items-center px-5">
          <span className="text-lg font-semibold tracking-tight">Lovd</span>
          <span className="ml-2 rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            staging
          </span>
        </Link>
        <Separator />
        <nav className="flex-1 space-y-1 p-3">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition",
                  item.disabled && "pointer-events-none opacity-40",
                  isActive
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {item.disabled && (
                <span className="ml-auto text-[10px] uppercase text-muted-foreground">
                  Soon
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <Separator />
        <div className="p-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex w-full items-center gap-3 rounded-md p-2 text-sm hover:bg-sidebar-accent">
                <Avatar className="h-8 w-8">
                  <AvatarFallback>{initial}</AvatarFallback>
                </Avatar>
                <div className="flex flex-1 flex-col items-start text-left">
                  <span className="truncate font-medium">
                    {session?.user?.name ?? session?.user?.email}
                  </span>
                  {session?.user?.name && (
                    <span className="truncate text-xs text-muted-foreground">
                      {session.user.email}
                    </span>
                  )}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{session?.user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>
                <Settings className="mr-2 h-4 w-4" />
                Account settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await signOut();
                  window.location.href = "/sign-in";
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="flex flex-col">
        <Topbar />
        <div className="flex-1 overflow-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Topbar() {
  return (
    <header className="flex h-14 items-center justify-between border-b border-border px-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>Workspace</span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" disabled className="text-xs">
          ⌘K
        </Button>
        <Button size="sm" disabled>
          <Plus className="mr-1 h-4 w-4" />
          New
        </Button>
      </div>
    </header>
  );
}
